import { FC, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { BannerElementLayout, BannerElementStyles, BannerSummary } from '../types/BannerAdmin';
import { Pill } from '../../../../components/shared/Pill';
import BannerPreview from './BannerPreview';

type Props = {
  banners: BannerSummary[];
  loading: boolean;
  searchQuery: string;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onEdit: (b: BannerSummary) => void;
  onDelete: (b: BannerSummary) => void;
  onCreate: () => void;
};

const ITEMS_PER_PAGE = 8;

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 4,
};

function tryParseJson<T>(json?: string): T | undefined {
  if (!json) return undefined;
  try { return JSON.parse(json) as T; }
  catch { return undefined; }
}

type BannerCategory = 'live' | 'draft' | 'history';

function categorizeBanner(banner: BannerSummary): BannerCategory {
  const now = new Date();
  const start = banner.scheduleStartUtc ? new Date(banner.scheduleStartUtc) : null;
  const end = banner.scheduleEndUtc ? new Date(banner.scheduleEndUtc) : null;

  if (banner.deletedDateTimeUtc) return 'history';

  if (end && end < now) return 'history';

  if (!banner.isActive) return 'draft';

  if (start && start > now) return 'draft';

  return 'live';
}

const BannersTable: FC<Props> = ({
  banners,
  loading,
  searchQuery,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onEdit,
  onDelete,
  onCreate,
}) => {
  const { t } = useTranslation('banners');
  const theme = useTheme();

  const { live, drafts, history } = useMemo(() => {
    const grouped = { live: [] as BannerSummary[], drafts: [] as BannerSummary[], history: [] as BannerSummary[] };
    for (const banner of banners) {
      const cat = categorizeBanner(banner);
      if (cat === 'live') grouped.live.push(banner);
      else if (cat === 'draft') grouped.drafts.push(banner);
      else grouped.history.push(banner);
    }
    return grouped;
  }, [banners]);

  const [livePage, setLivePage] = useState(0);
  const [draftPage, setDraftPage] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);

  if (!loading && banners.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 10,
          px: 4,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '16px',
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
          }}
        >
          <ImageOutlinedIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
        </Box>
        <Typography
          variant="textLG"
          sx={{ fontWeight: 700, mb: 0.5 }}
        >
          {t('noBanners')}
        </Typography>
        <Typography
          variant="textSM"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 340, textAlign: 'center' }}
        >
          {t('noBannersDescription')}
        </Typography>
        <Button
          variant="primary"
          size="lg"
          onClick={onCreate}
        >
          {t('createBanner')}
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ mt: 2, ...GRID_SX }}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Box
            key={i}
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              border: `1px solid ${theme.palette.custom.gray[200]}`,
            }}
          >
            <Skeleton
              variant="rectangular"
              height={150}
            />
            <Box sx={{ p: 2 }}>
              <Skeleton
                width="60%"
                height={22}
              />
              <Skeleton
                width="40%"
                height={16}
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (searchQuery && banners.length === 0) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center', py: 6 }}>
        <Typography
          variant="textMD"
          sx={{ fontWeight: 600, color: theme.palette.custom.gray[600] }}
        >
          {t('noSearchResults')}
        </Typography>
        <Typography
          variant="textSM"
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {t('noSearchResultsDescription')}
        </Typography>
      </Box>
    );
  }

  const renderBannerCard = (banner: BannerSummary, category: BannerCategory) => {
    const layout = tryParseJson<BannerElementLayout>(banner.elementLayoutJson);
    const elementStyles = tryParseJson<BannerElementStyles>(banner.elementStylesJson);
    const isDraft = category === 'draft';
    const isHistory = category === 'history';

    return (
      <Box
        key={banner.id}
        onClick={() => onEdit(banner)}
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          opacity: isDraft ? 0.5 : isHistory ? 0.6 : 1,
          filter: isDraft ? 'grayscale(0.6)' : isHistory ? 'grayscale(0.3)' : 'none',
          transition: 'all 0.15s ease',
          border: `1px solid ${theme.palette.custom.gray[200]}`,
          '&:hover': {
            opacity: 1,
            filter: 'none',
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <BannerPreview
            data={{
              title: banner.title,
              subtitle: banner.subtitle,
              tag: banner.tagText,
              tagColor: banner.tagColor,
              ctaText: banner.ctaText,
              imageUrl: banner.imageUrl,
              bgColor: banner.backgroundColor,
              textColor: banner.textColor,
            }}
            layout={layout}
            elementStyles={elementStyles}
          />

          {(banner.assignedSlotKeys?.length ?? 0) > 0 && (
            <Chip
              label={`${banner.assignedSlotKeys![0]}${banner.priority ? ` · #${banner.priority}` : ''}`}
              size="small"
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.85),
                color: theme.palette.common.white,
                fontWeight: 700,
                fontSize: '0.6rem',
                height: 20,
                borderRadius: '4px',
              }}
            />
          )}

          {isDraft && (
            <Chip
              label={t('draft')}
              size="small"
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.9),
                color: theme.palette.common.white,
                fontWeight: 700,
                fontSize: '0.6rem',
                height: 20,
                borderRadius: '4px',
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            backgroundColor: theme.palette.common.white,
            borderTop: `1px solid ${theme.palette.custom.gray[100]}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            <Typography
              variant="textXS"
              sx={{ color: theme.palette.custom.gray[500], fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {banner.title || t('untitled')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            <Tooltip
              title={t('edit')}
              arrow
            >
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onEdit(banner); }}
                sx={{
                  color: theme.palette.custom.gray[400],
                  '&:hover': { color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={t('delete')}
              arrow
            >
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDelete(banner); }}
                sx={{
                  color: theme.palette.custom.gray[400],
                  '&:hover': { color: theme.palette.error.main, backgroundColor: alpha(theme.palette.error.main, 0.08) },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSection = (
    title: string,
    description: string,
    items: BannerSummary[],
    category: BannerCategory,
    page: number,
    setPage: (_p: number) => void,
    icon?: React.ReactNode,
  ) => {
    if (items.length === 0) return null;

    const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
    const pagedItems = items.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);
    const hasPrev = page > 0;
    const hasNext = page < totalPages - 1;

    return (
      <Box sx={{ mb: 7 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {icon}
            <Typography
              variant="textMD"
              sx={{ fontWeight: 700 }}
            >
              {title}
            </Typography>
            <Pill text={`${items.length}`} />
          </Box>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="textXS"
                sx={{ color: theme.palette.custom.gray[500] }}
              >
                {page + 1} / {totalPages}
              </Typography>
              <IconButton
                size="small"
                disabled={!hasPrev}
                onClick={() => setPage(page - 1)}
                sx={{
                  border: `1px solid ${theme.palette.custom.gray[200]}`,
                  borderRadius: '8px',
                  width: 28,
                  height: 28,
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                disabled={!hasNext}
                onClick={() => setPage(page + 1)}
                sx={{
                  border: `1px solid ${theme.palette.custom.gray[200]}`,
                  borderRadius: '8px',
                  width: 28,
                  height: 28,
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>
        <Typography
          variant="textXS"
          color="text.secondary"
          sx={{ mb: 3, ml: icon ? 4.5 : 0 }}
        >
          {description}
        </Typography>
        <Box sx={GRID_SX}>
          {pagedItems.map((banner) => renderBannerCard(banner, category))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 4 }}>
      {renderSection(
        t('liveBanners'),
        t('liveBannersDescription'),
        live,
        'live',
        livePage,
        setLivePage,
      )}

      {renderSection(
        t('draftBanners'),
        t('draftBannersDescription'),
        drafts,
        'draft',
        draftPage,
        setDraftPage,
      )}

      {renderSection(
        t('historyBanners'),
        t('historyBannersDescription'),
        history,
        'history',
        historyPage,
        setHistoryPage,
        <HistoryOutlinedIcon sx={{ fontSize: 18, color: theme.palette.custom.gray[500] }} />,
      )}

      {totalCount > pageSize && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3, mb: 2 }}>
          <IconButton
            size="small"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            sx={{
              border: `1px solid ${theme.palette.custom.gray[200]}`,
              borderRadius: '8px',
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="textSM"
            sx={{ color: theme.palette.custom.gray[600] }}
          >
            {t('pageOf', { current: page, total: Math.ceil(totalCount / pageSize) })}
          </Typography>
          <IconButton
            size="small"
            disabled={page >= Math.ceil(totalCount / pageSize)}
            onClick={() => onPageChange(page + 1)}
            sx={{
              border: `1px solid ${theme.palette.custom.gray[200]}`,
              borderRadius: '8px',
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default BannersTable;
